import React, { useState, useEffect } from 'react';
import { AboutModal, Icons } from '@ohif/ui-next';
import detect from 'browser-detect';
import { useTranslation } from 'react-i18next';

/**
 * [CurveBeamAI] Resolve OS display. browser-detect reports Windows 11 as "Windows 10" (UA uses NT 10.0);
 * User-Agent Client Hints (getHighEntropyValues) can distinguish Windows 11 by platformVersion.
 */
function useResolvedOS(initialOs: string): string {
  const [os, setOs] = useState(initialOs);
  useEffect(() => {
    const ua = (navigator as any).userAgentData;
    if (!ua?.getHighEntropyValues || ua?.platform !== 'Windows') return;
    ua.getHighEntropyValues(['platformVersion'])
      .then(vals => {
        if (vals?.platformVersion) {
          const major = parseInt(vals.platformVersion.split('.')[0], 10);
          if (major >= 13) setOs('Windows 11');
        }
      })
      .catch(() => {
        /* Client Hints not supported or denied */
      });
  }, []); // One-time enhancement; initialOs is only used as fallback
  return os;
}

/**
 * [CurveBeamAI] Detect CurveBeam config (apiEndpoint + appConfigService).
 * Used to apply CurveBeam branding/customizations elsewhere.
 */
export function isCurveBeamConfig(): boolean {
  if (typeof window === 'undefined') return false;
  const cfg = (window as any).config;
  return !!(cfg?.appConfigService?.applicationId && cfg?.apiEndpoint);
}

/** [CurveBeamAI] External link with optional icon; used for Important links and version rows */
const ExternalLinkItem = ({
  href,
  children,
  showIcon = true,
}: {
  href: string;
  children: React.ReactNode;
  showIcon?: boolean;
}) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="text-primary-main hover:text-primary-light flex items-center text-base"
  >
    <span>{children}</span>
    {showIcon && (
      <Icons.ByName
        name="external-link"
        className="ml-2 h-4 w-4 shrink-0"
      />
    )}
  </a>
);

/** [CurveBeamAI] Key-value row; value renders as link when `link` prop is provided */
const VersionRow = ({ title, value, link }: { title: string; value: string; link?: string }) => (
  <div className="mb-4 flex justify-between">
    <span className="text-foreground w-48 shrink-0 text-sm">{title}</span>
    {link ? (
      <ExternalLinkItem href={link}>{value}</ExternalLinkItem>
    ) : (
      <span className="text-foreground text-sm">{value}</span>
    )}
  </div>
);

/** [CurveBeamAI] Section header with bottom border */
const SectionHeader = ({ children }: { children: React.ReactNode }) => (
  <div className="border-primary-border mb-3 border-b pb-3">
    <h4 className="text-primary-light text-base font-semibold leading-tight">{children}</h4>
  </div>
);

/**
 * [CurveBeamAI] About modal with CurveBeam branding.
 * CurveBeamAI links, app/OS/browser versions, and base OHIF version.
 */
export function AboutModalCurveBeam() {
  const { t } = useTranslation('AboutModal');
  const { os: osRaw, version, name } = detect();
  const os = useResolvedOS(osRaw);
  const browser = `${name[0].toUpperCase()}${name.slice(1)} ${version}`;

  // Config values from window.config (set by app-config.js / config API)
  const cfg = (window as any)?.config ?? {};
  const versionNumber = cfg.version ?? process.env.VERSION_NUMBER ?? '-';
  const dmpHomeURL = cfg.dmpHomeURL ?? '';

  // Base OHIF version comes from the build, not AppConfig: version.txt is bumped by every upstream
  // tag merge and compiled in as VERSION_NUMBER, so it cannot go stale the way a hand-maintained
  // config value did. The file ends in a newline, hence trim(). OHIF publishes one release-notes
  // page per minor version (e.g. /release-notes/3p13/).
  const baseOHIFversion = (process.env.VERSION_NUMBER ?? '').trim();
  const [ohifMajor, ohifMinor] = baseOHIFversion.split('.');
  const baseOHIFReleaseNotesLink = ohifMinor
    ? `https://ohif.org/release-notes/${ohifMajor}p${ohifMinor}/`
    : '';

  return (
    <AboutModal className="min-h-[290px] w-[640px] text-left">
      <SectionHeader>{t('Important links')}</SectionHeader>
      <div className="mb-8 flex flex-wrap gap-x-4 gap-y-2">
        <ExternalLinkItem href="https://curvebeamai.com/">
          Visit the CurvebeamAI website
        </ExternalLinkItem>
        {dmpHomeURL && (
          <ExternalLinkItem href={dmpHomeURL}>
            Visit the CurvebeamAI Data Management Platform (DMP)
          </ExternalLinkItem>
        )}
      </div>

      <div className="pt-6">
        <SectionHeader>{t('Version information')}</SectionHeader>
        <div className="mb-8 flex flex-col">
          <VersionRow
            title={t('Version number')}
            value={versionNumber}
          />
          <VersionRow
            title={t('Browser')}
            value={browser}
          />
          <VersionRow
            title={t('OS')}
            value={os}
          />
          <VersionRow
            title="Base OHIF version"
            value={baseOHIFversion}
            link={baseOHIFReleaseNotesLink}
          />
        </div>
      </div>
    </AboutModal>
  );
}

/**
 * [CurveBeamAI] Returns customization object to override OHIF's default About modal.
 * Use with customizationService.setCustomizations() after app init (appBootstrap).
 *
 * OHIF reads metadata (title, menuTitle, etc.) from the component function;
 * defineProperty/getter ensures modal shell reads current values when opened.
 */
export function getCurveBeamAboutModalCustomization() {
  const AboutModalContent = () => <AboutModalCurveBeam />;
  Object.defineProperty(AboutModalContent, 'title', {
    get: () => 'About CurveBeamAI Viewer',
    configurable: true,
  });
  (AboutModalContent as any).menuTitle = 'About';
  (AboutModalContent as any).containerClassName = 'max-w-2xl min-h-[290px]';
  Object.defineProperty(AboutModalContent, 'titleAs', {
    get: () => 'h6' as const,
    configurable: true,
  });
  Object.defineProperty(AboutModalContent, 'titleClassName', {
    get: () => 'text-primary-light text-base font-semibold leading-tight',
    configurable: true,
  });
  return { 'ohif.aboutModal': AboutModalContent };
}
